import { useEffect, useRef, useState } from 'react';
import { useNotificationStore } from '../../stores/notificationStore';

/**
 * Speaks every toast to a screen reader.
 *
 *
 * WHY
 *
 * The app confirmed its work only visually. Saving a diet order, queuing a
 * report, switching facility — all of it produced a toast and otherwise total
 * silence. A blind dietitian pressed Save and had no way to know whether the
 * order had been written, failed validation, or hit the server and errored.
 * For clinical data entry that is not an inconvenience; the safe response to
 * silence is to save again, and saving again is its own hazard.
 *
 * There were six aria-live regions in the entire application before this.
 *
 *
 * WHY HERE RATHER THAN AT THE CALL SITES
 *
 * 1,308 places call `addNotification`. Wiring announcements individually would
 * mean 1,308 chances to forget, and the ones that got forgotten would be
 * invisible — silence looks exactly like success. Every toast already funnels
 * through one store, so one subscriber covers all of them and anything added
 * later is announced without the author having to know this exists.
 *
 *
 * TWO REGIONS, NOT ONE
 *
 * `aria-live` is read when the region's CONTENT changes; changing the
 * politeness attribute at the same time is unreliable across screen readers.
 * So errors and warnings own a permanently assertive region and everything
 * else a permanently polite one. Assertive interrupts what the user is
 * reading, which is right for "Save failed" and wrong for "Report queued".
 *
 * The text is cleared before the next message is written. Without that, two
 * identical messages in a row — "Required: Mealtime" twice, which is exactly
 * what happens when someone fixes the wrong field — are one unchanged string,
 * and the second is never announced.
 */

/** Politeness is fixed per region; the type decides which region gets the text. */
function isUrgent(type: string): boolean {
  return type === 'error' || type === 'warning';
}

export default function LiveAnnouncer() {
  // Reads the store's announcement signal rather than diffing the toast list.
  // The list is the wrong source: `addNotification` suppresses an identical
  // message within 5 seconds, so a repeated validation error never reaches it —
  // and for a screen reader a suppressed announcement is silence, which reads
  // as success. The signal fires on every call and carries a sequence number,
  // so an identical repeat is still an observable change.
  const signal = useNotificationStore((s) => s.lastAnnouncement);

  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!signal) return;
    const setter = isUrgent(signal.type) ? setAssertive : setPolite;

    // Blank first: aria-live fires on content CHANGE, so writing the same
    // string twice is not a change and the second is never spoken. "Required:
    // Mealtime" twice is exactly what happens when someone corrects the wrong
    // field.
    setter('');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setter(signal.message), 60);
    // seq is what makes a repeated identical message re-run this effect.
  }, [signal?.seq, signal?.message, signal?.type]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return (
    <>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {polite}
      </div>
      <div className="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
        {assertive}
      </div>
    </>
  );
}
